import { Injectable } from '@angular/core';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { ColDef } from '../interfaces/col-def.interface';
import { ExportOptions } from '../interfaces/grid-api.interface';
import { getNestedValue } from '../utils/field-accessor.util';

/**
 * Grid Export Service
 * Handles CSV and Excel export functionality for Smart Grid
 */
@Injectable({
  providedIn: 'root',
})
export class GridExportService {
  /**
   * Export data to CSV
   * @param data - Data to export
   * @param columnDefs - Column definitions
   * @param options - Export options
   */
  exportToCsv<T>(
    data: T[],
    columnDefs: ColDef<T>[],
    options: ExportOptions = {}
  ): void {
    const {
      includeHeaders = true,
      columns,
      dataScope = 'displayed',
      filename = 'export',
      columnSeparator = ',',
      processValue,
      processHeader,
    } = options;

    // Filter columns to export
    const columnsToExport = this.getColumnsToExport(columnDefs, columns);

    // Build CSV content
    const csvRows: string[] = [];

    // Add headers
    if (includeHeaders) {
      const headers = columnsToExport.map((colDef) => {
        const headerName = colDef.headerName || this.humanize(colDef.field as string);
        const processedHeader = processHeader
          ? processHeader(headerName, colDef.field as string)
          : headerName;
        return this.escapeCsvValue(processedHeader, columnSeparator);
      });
      csvRows.push(headers.join(columnSeparator));
    }

    // Add data rows
    data.forEach((row) => {
      const values = columnsToExport.map((colDef) => {
        const field = colDef.field as string;
        let value = getNestedValue(row, field);

        // Apply value formatter if exists
        if (colDef.valueFormatter && value !== null && value !== undefined) {
          value = colDef.valueFormatter(value, row);
        }

        // Apply custom process value
        if (processValue) {
          value = processValue(value, field);
        }

        return this.escapeCsvValue(String(value ?? ''), columnSeparator);
      });
      csvRows.push(values.join(columnSeparator));
    });

    // Create blob and download
    const csvContent = csvRows.join('\n');
    const blob = new Blob(['\ufeff' + csvContent], {
      type: 'text/csv;charset=utf-8;',
    }); // UTF-8 BOM for Excel compatibility
    saveAs(blob, `${filename}.csv`);
  }

  /**
   * Export data to Excel
   * @param data - Data to export
   * @param columnDefs - Column definitions
   * @param options - Export options
   */
  async exportToExcel<T>(
    data: T[],
    columnDefs: ColDef<T>[],
    options: ExportOptions = {}
  ): Promise<void> {
    const {
      includeHeaders = true,
      columns,
      filename = 'export',
      processValue,
      processHeader,
    } = options;

    // Filter columns to export
    const columnsToExport = this.getColumnsToExport(columnDefs, columns);

    // Create workbook and worksheet
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Data');

    // Add headers
    if (includeHeaders) {
      const headers = columnsToExport.map((colDef) => {
        const headerName = colDef.headerName || this.humanize(colDef.field as string);
        return processHeader
          ? processHeader(headerName, colDef.field as string)
          : headerName;
      });

      const headerRow = worksheet.addRow(headers);

      // Style headers
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4472C4' },
      };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
      headerRow.height = 25;
    }

    // Add data rows
    data.forEach((row) => {
      const values = columnsToExport.map((colDef) => {
        const field = colDef.field as string;
        let value = getNestedValue(row, field);

        // Apply value formatter if exists
        if (colDef.valueFormatter && value !== null && value !== undefined) {
          value = colDef.valueFormatter(value, row);
        }

        // Apply custom process value
        if (processValue) {
          value = processValue(value, field);
        }

        return value ?? '';
      });
      worksheet.addRow(values);
    });

    // Auto-size columns
    columnsToExport.forEach((colDef, index) => {
      const column = worksheet.getColumn(index + 1);
      const headerName = colDef.headerName || this.humanize(colDef.field as string);
      const maxLength = Math.max(
        headerName.length,
        ...data.map((row) => {
          let value = getNestedValue(row, colDef.field as string);
          if (colDef.valueFormatter && value !== null && value !== undefined) {
            value = colDef.valueFormatter(value, row);
          }
          return String(value ?? '').length;
        })
      );
      column.width = Math.min(Math.max(maxLength + 2, 10), 50);
    });

    // Add borders to all cells
    worksheet.eachRow((row, rowNumber) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
    });

    // Generate buffer and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `${filename}.xlsx`);
  }

  /**
   * Get columns to export based on options
   */
  private getColumnsToExport<T>(
    columnDefs: ColDef<T>[],
    columns?: string[]
  ): ColDef<T>[] {
    if (columns && columns.length > 0) {
      // Export specific columns
      return columnDefs.filter((col) => columns.includes(col.field as string));
    } else {
      // Export all visible columns
      return columnDefs.filter((col) => !col.hide && col.field);
    }
  }

  /**
   * Escape CSV value
   */
  private escapeCsvValue(value: string, separator: string): string {
    if (value.includes(separator) || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  }

  /**
   * Humanize field name for auto-generated headers
   */
  private humanize(field: string): string {
    if (!field) return '';

    return field
      .split('.')
      .pop() // Get last part of nested path
      ?.replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
      .trim() || '';
  }
}
